"use client"
import { useEffect, useState, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  FormControl,
  FormLabel,
  IconButton,
  Text,
  TableContainer,
  Container,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Select,
  useToast,
} from "@chakra-ui/react"
import { EditIcon, DeleteIcon, AddIcon, ChevronDownIcon } from "@chakra-ui/icons"
import {
  fetchEmployersForDropdownAsync,
  selectAllEmployers,
} from "../../EmployerData/EmployerDataSlice"
import {
  addSubscriptionTopUpAsync,
  fetchSubscriptionTopUpAsync,
  updateSubscriptionTopUpAsync,
  deleteSubscriptionTopUpAsync,
  selectSubscriptionTopUp,
} from "../../Subscriptions/SubscriptionTopUp/SubscriptionTopUpSlice"

const initialSubscriptionState = {
  user_uuid: "",
  type: "job_post",
  value: "",
}

const planTypes = [
  "job_post",
  "urgent_hiring_tag",
  "candidate_unlock",
  "job_published",
  "notification_via_email_sms_whatsapp",
  "job_boosts",
  "smart_boost_via_email_sms_whatsapp",
  "dedicated_relationship_manager",
  "campion_via_email_sms_whatsapp",
  "multiple_user_login",
  "reports",
  "company_branding",
]

// Helper function to format the plan type for display
const formatPlanType = (type) => {
  if (!type) return ""; // Handle undefined/null cases
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function SubscriptionTopUp() {
  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const toast = useToast()

  const [newSubscription, setNewSubscription] = useState(initialSubscriptionState)
  const [editingSubscription, setEditingSubscription] = useState(initialSubscriptionState)
  const [selectedEmployerName, setSelectedEmployerName] = useState("")

  const allEmployers = useSelector(selectAllEmployers)
  const employerDropdownPagination = useSelector((state) => state.employerData.employerDropdownPagination)
  const isFetchingMoreEmployers = useSelector((state) => state.employerData.isFetchingMoreEmployers)
  const subscriptions = useSelector(selectSubscriptionTopUp)
  const dispatch = useDispatch()

  const menuListRef = useRef(null)

  useEffect(() => {
    dispatch(fetchSubscriptionTopUpAsync())
    dispatch(fetchEmployersForDropdownAsync(1))
  }, [dispatch])

  const showToast = (title, description, status) => {
    toast({
      title,
      description,
      status,
      duration: 5000,
      isClosable: true,
      position: "top-right",
    })
  }

  const handleAddSubscription = async () => {
    try {
      await dispatch(addSubscriptionTopUpAsync(newSubscription))
      showToast("Success", "Subscription added successfully", "success")
      resetAndCloseAddModal()
      dispatch(fetchSubscriptionTopUpAsync())
    } catch (error) {
      console.error("Error adding subscription:", error)
      showToast("Error", "Failed to add subscription", "error")
    }
  }

  const handleUpdateSubscription = async () => {
    try {
      const updateSubscriptiondata = {
        type: editingSubscription.type,
        value: editingSubscription.value,
      }
      await dispatch(
        updateSubscriptionTopUpAsync({
          id: editingSubscription.uuid,
          updateSubscriptiondata,
        }),
      )
      showToast("Success", "Subscription updated successfully", "success")
      resetAndCloseEditModal()
      dispatch(fetchSubscriptionTopUpAsync())
    } catch (error) {
      console.error("Error updating subscription:", error)
      showToast("Error", "Failed to update subscription", "error")
    }
  }

  const handleDeleteSubscription = async (id) => {
    if (window.confirm("Are you sure you want to delete this subscription?")) {
      try {
        await dispatch(deleteSubscriptionTopUpAsync(id))
        showToast("Success", "Subscription deleted successfully", "success")
        dispatch(fetchSubscriptionTopUpAsync())
      } catch (error) {
        console.error("Error deleting subscription:", error)
        showToast("Error", "Failed to delete subscription", "error")
      }
    }
  }

  const openEditModal = (subscription) => {
    setEditingSubscription(subscription)
    setSelectedEmployerName(subscription.employer?.name || subscription.employer?.email || "Select a user")
    onEditOpen()
  }

  const resetAndCloseAddModal = () => {
    setNewSubscription(initialSubscriptionState)
    setSelectedEmployerName("")
    onAddClose()
  }

  const resetAndCloseEditModal = () => {
    setEditingSubscription(initialSubscriptionState)
    setSelectedEmployerName("")
    onEditClose()
  }

  const handleEmployerSelect = (user) => {
    setNewSubscription({
      ...newSubscription,
      user_uuid: user.uuid,
    })
    setSelectedEmployerName(user.name || user.email)
  }

  const handleEditEmployerSelect = (user) => {
    setEditingSubscription({
      ...editingSubscription,
      user_uuid: user.uuid,
    })
    setSelectedEmployerName(user.name || user.email)
  }

  const handleScroll = () => {
    if (menuListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = menuListRef.current
      if (
        scrollTop + clientHeight >= scrollHeight - 50 &&
        employerDropdownPagination.hasMore &&
        !isFetchingMoreEmployers
      ) {
        dispatch(fetchEmployersForDropdownAsync(employerDropdownPagination.currentPage + 1))
      }
    }
  }

  return (
    <div className=" bg-adminbg rounded " style={{ fontFamily: "Inter, sans-serif" }}>
      <Container maxW="container.2xl" className="bg-white my-2 rounded-md">
        <Flex justifyContent="space-between" alignItems="center" mb={6}>
          <h2 className="text-xl text-black font-semibold px-3 py-7">Subscription Plans Top Up</h2>
          <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={onAddOpen}>
            Add New Plan
          </Button>
        </Flex>

        {/* Add New Subscription Modal */}
        <Modal isOpen={isAddOpen} onClose={resetAndCloseAddModal} size="5xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Add New Subscription</ModalHeader>
            <ModalBody>
              <FormControl>
                <FormLabel>User</FormLabel>
                <Menu>
                  <MenuButton as={Button} rightIcon={<ChevronDownIcon />} width="100%">
                    {selectedEmployerName || "Select a user"}
                  </MenuButton>
                  <MenuList maxH="200px" overflowY="auto" ref={menuListRef} onScroll={handleScroll}>
                    {allEmployers.length === 0 && !isFetchingMoreEmployers ? (
                      <MenuItem isDisabled>No employers found.</MenuItem>
                    ) : (
                      allEmployers.map((user) => (
                        <MenuItem key={user.uuid} onClick={() => handleEmployerSelect(user)}>
                          <div className="gap-2 flex justify-between p-2 bg-gray-50 rounded-md">
                            <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-md font-medium">
                              {user?.name}
                            </span>
                            <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-md font-medium">
                              {user?.email}
                            </span>
                          </div>
                        </MenuItem>
                      ))
                    )}
                    {isFetchingMoreEmployers && (
                      <MenuItem isDisabled>
                        <Flex align="center" justify="center" width="100%">
                          <Spinner size="sm" mr={2} /> Loading more...
                        </Flex>
                      </MenuItem>
                    )}
                    {!employerDropdownPagination.hasMore && allEmployers.length > 0 && (
                      <MenuItem isDisabled>No more employers</MenuItem>
                    )}
                  </MenuList>
                </Menu>
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Type</FormLabel>
                <Select
                  value={newSubscription.type}
                  onChange={(e) =>
                    setNewSubscription({
                      ...newSubscription,
                      type: e.target.value,
                    })
                  }
                >
                  {planTypes.map((type) => (
                    <option key={type} value={type}>
                      {formatPlanType(type)}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Value</FormLabel>
                <Input
                  value={newSubscription.value}
                  type="number"
                  onChange={(e) =>
                    setNewSubscription({
                      ...newSubscription,
                      value: e.target.value,
                    })
                  }
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={handleAddSubscription}>
                Add Subscription
              </Button>
              <Button variant="ghost" onClick={resetAndCloseAddModal}>
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Edit Subscription Modal */}
        <Modal isOpen={isEditOpen} onClose={resetAndCloseEditModal}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Edit Subscription</ModalHeader>
            <ModalBody>
              <FormControl>
                <FormLabel>User</FormLabel>
                {editingSubscription?.employer ? (
                  <Text>{editingSubscription?.employer?.name || editingSubscription?.employer?.email}</Text>
                ) : (
                  <Menu>
                    <MenuButton as={Button} rightIcon={<ChevronDownIcon />} width="100%">
                      {selectedEmployerName || "Select a user"}
                    </MenuButton>
                    <MenuList maxH="200px" overflowY="auto" ref={menuListRef} onScroll={handleScroll}>
                      {allEmployers.length === 0 && !isFetchingMoreEmployers ? (
                        <MenuItem isDisabled>No employers found.</MenuItem>
                      ) : (
                        allEmployers.map((user) => (
                          <MenuItem key={user.uuid} onClick={() => handleEditEmployerSelect(user)}>
                            {user.name || user.email}
                          </MenuItem>
                        ))
                      )}
                      {isFetchingMoreEmployers && (
                        <MenuItem isDisabled>
                          <Flex align="center" justify="center" width="100%">
                            <Spinner size="sm" mr={2} /> Loading more...
                          </Flex>
                        </MenuItem>
                      )}
                      {!employerDropdownPagination.hasMore && allEmployers.length > 0 && (
                        <MenuItem isDisabled>No more employers</MenuItem>
                      )}
                    </MenuList>
                  </Menu>
                )}
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Type</FormLabel>
                <Select
                  value={editingSubscription.type}
                  onChange={(e) =>
                    setEditingSubscription({
                      ...editingSubscription,
                      type: e.target.value,
                    })
                  }
                >
                  {planTypes.map((type) => (
                    <option key={type} value={type}>
                      {formatPlanType(type)}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Value</FormLabel>
                <Input
                  value={editingSubscription.value}
                  type="number"
                  onChange={(e) =>
                    setEditingSubscription({
                      ...editingSubscription,
                      value: e.target.value,
                    })
                  }
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={handleUpdateSubscription}>
                Update Subscription
              </Button>
              <Button variant="ghost" onClick={resetAndCloseEditModal}>
                Cancel
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Subscription Table */}
        <TableContainer>
          <Table colorScheme="gray" size="sm">
            <Thead className="bg-gray-200">
              <Tr>
                <Th>User</Th>
                <Th>Type</Th>
                <Th>Value</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {subscriptions.map((subscription) => (
                <Tr key={subscription.id}>
                  <Td className="capitalize">{subscription.employer?.name || subscription.employer?.email}</Td>
                  <Td>{formatPlanType(subscription.type)}</Td>
                  <Td>{subscription.value}</Td>
                  <Td>
                    <IconButton
                      aria-label="Edit subscription"
                      icon={<EditIcon />}
                      onClick={() => openEditModal(subscription)}
                      mr={2}
                    />
                    <IconButton
                      aria-label="Delete subscription"
                      icon={<DeleteIcon />}
                      onClick={() => handleDeleteSubscription(subscription.uuid)}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Container>
    </div>
  )
}